/**
 * The measurement id block — the single source for the vendor loaders and for the
 * privacy disclosure that describes them (docs/behavior/measure-legal-auth.md §1.1).
 *
 * Arming a vendor is a one-line change here. An id is used only when it is a
 * non-empty string after trimming; anything else leaves that vendor off.
 */
export interface MeasurementIds {
  /** Google Analytics 4 measurement id. */
  readonly ga4: string;
  /** Meta Pixel id — marketing; needed only before paid spend on Meta. */
  readonly meta: string;
  /** TikTok Pixel id — marketing. */
  readonly tiktok: string;
  /** X Pixel id — marketing. */
  readonly x: string;
  /**
   * X reports conversions against an EVENT id (tw-xxxxx-xxxxx) created in X Ads Manager,
   * not a free-text name. Empty: the base pixel still loads, the purchase conversion is not
   * reported. Nothing is invented to fill it.
   */
  readonly x_purchase_event: string;
}

/** Live ids as of 2026-09-11 (assets/omega-measure-ids.js). */
export const MEASUREMENT_IDS: MeasurementIds = {
  ga4: 'G-Q7YEL8VKCS',
  meta: '',
  tiktok: '',
  x: '',
  x_purchase_event: '',
};

/** Armed, trimmed ids. An empty string means "this vendor is off". */
export interface VendorConfig {
  readonly ga4: string;
  readonly meta: string;
  readonly tiktok: string;
  readonly x: string;
  readonly xPurchaseEvent: string;
}

/** `pick()` of omega-measure.js: a string that is non-empty after trimming, trimmed; otherwise ''. */
export function pickId(value: unknown): string {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : '';
}

export function vendorConfig(ids: Readonly<Partial<Record<keyof MeasurementIds, unknown>>>): VendorConfig {
  return {
    ga4: pickId(ids.ga4),
    meta: pickId(ids.meta),
    tiktok: pickId(ids.tiktok),
    x: pickId(ids.x),
    xPurchaseEvent: pickId(ids.x_purchase_event),
  };
}
